import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, message, Upload, Table, Tag } from 'antd';
import { UploadOutlined, FileExcelOutlined, ImportOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function WorkLogImport() {
  const navigate = useNavigate();
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);

  const handleFileUpload = (file) => {
    const fileType = file.name.toLowerCase();
    
    if (fileType.endsWith('.xlsx') || fileType.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          
          // 转换数据格式 - 所有字段都从Excel读取
          const logs = jsonData.map(row => ({
            projectName: row['项目'] || row['project'] || row['projectName'] || '',
            date: formatDate(row['日期'] || row['date'] || ''),
            focus: row['工作重点'] || row['focus'] || '',
            detail: row['详细内容'] || row['detail'] || row['工作内容'] || '',
            remarks: row['其他说明'] || row['remarks'] || row['备注'] || ''
          })).filter(item => item.projectName && item.date);
          
          setImportData(logs);
          message.success(`成功解析 ${logs.length} 条记录`);
        } catch (err) {
          message.error('Excel解析失败: ' + err.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      message.error('不支持的文件格式，请上传Excel文件');
    }
    
    return false;
  };

  // 日期格式化：支持多种格式
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // 如果是数字（Excel日期序列号）
    if (typeof dateStr === 'number') {
      const date = new Date((dateStr - 25569) * 86400 * 1000);
      return date.toISOString().split('T')[0];
    }
    
    // 如果是字符串
    const str = String(dateStr).trim();
    
    // 尝试匹配 YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return str.substring(0, 10);
    }
    
    // 尝试匹配 YYYY/MM/DD
    if (/^\d{4}\/\d{2}\/\d{2}/.test(str)) {
      return str.replace(/\//g, '-').substring(0, 10);
    }
    
    // 尝试匹配 YYYYMMDD
    if (/^\d{8}$/.test(str)) {
      return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
    }
    
    // 尝试用 Date 解析
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return str;
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      message.error('没有可导入的数据');
      return;
    }

    setImporting(true);
    try {
      const resp = await fetch(`${API_URL}/api/work-logs/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': localStorage.getItem('notes_token')
        },
        body: JSON.stringify({ logs: importData })
      });

      if (resp.ok) {
        const result = await resp.json();
        message.success(`成功导入 ${result.count} 条日志`);
        setImportData([]);
      } else {
        const err = await resp.json();
        message.error(err.error || '导入失败');
      }
    } catch (e) {
      message.error('导入失败');
    }
    setImporting(false);
  };

  const columns = [
    {
      title: '项目',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 150
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120
    },
    {
      title: '工作重点',
      dataIndex: 'focus',
      key: 'focus',
      width: 200,
      ellipsis: true
    },
    {
      title: '详细内容',
      dataIndex: 'detail',
      key: 'detail',
      width: 200,
      ellipsis: true
    },
    {
      title: '其他说明',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true
    }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')} style={{ marginRight: 16 }}>
          返回
        </Button>
        <h2 style={{ margin: 0 }}><ImportOutlined /> 日志导入</h2>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleFileUpload}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />} style={{ marginRight: 8 }}>
              <FileExcelOutlined /> 选择Excel文件
            </Button>
          </Upload>
          <span style={{ color: '#999', fontSize: 12 }}>
            支持格式：Excel (.xlsx, .xls)
          </span>
        </div>

        {importData.length > 0 && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag color="green">已解析 {importData.length} 条记录</Tag>
              <Button 
                type="primary" 
                icon={<ImportOutlined />} 
                onClick={handleImport}
                loading={importing}
              >
                确认导入
              </Button>
            </div>
            <Table
              dataSource={importData.map((item, idx) => ({ ...item, key: idx }))}
              columns={columns}
              pagination={{ pageSize: 10 }}
              size="small"
              bordered
            />
          </div>
        )}
      </Card>

      <Card title="Excel模板说明">
        <p>Excel文件需包含以下列（列名可自定义）：</p>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ border: '1px solid #e8e8e8', padding: '8px 12px', textAlign: 'left' }}>列名</th>
              <th style={{ border: '1px solid #e8e8e8', padding: '8px 12px', textAlign: 'left' }}>别名</th>
              <th style={{ border: '1px solid #e8e8e8', padding: '8px 12px', textAlign: 'left' }}>说明</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}><strong>项目</strong></td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>project, projectName</td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>项目名称，必填</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}><strong>日期</strong></td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>date</td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>支持 YYYY-MM-DD、YYYY/MM/DD、YYYYMMDD，必填</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}><strong>工作重点</strong></td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>focus</td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>当日工作重点</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}><strong>详细内容</strong></td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>detail, 工作内容</td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>详细工作内容</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}><strong>其他说明</strong></td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>remarks, 备注</td>
              <td style={{ border: '1px solid #e8e8e8', padding: '8px 12px' }}>问题/备注/重要事项</td>
            </tr>
          </tbody>
        </table>
        <p style={{ color: '#999', marginTop: 12 }}>
          示例：第一行为表头，从第二行开始为数据。每行可以有不同的项目和日期，支持批量导入多天数据。
        </p>
      </Card>
    </div>
  );
}
